import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';

const fieldClass = 'w-full px-4 py-3 bg-deepspace/50 border border-dark-border/50 rounded-xl text-text-primary placeholder-text-muted focus:border-cyber-blue/50 focus:outline-none transition-colors';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    code: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const onChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const { isLoading, mutate } = useMutation(
    async () => apiClient.post('/api/update-password', form),
    {
      onSuccess: (res) => {
        localStorage.setItem('notification', JSON.stringify({ message: `${res.data.message}`, type: 'success' }));
        window.dispatchEvent(new Event('newMessage'));
        window.dispatchEvent(new Event('processed'));
        navigate('/login');
      },
      onError: (err) => {
        const data = err.response?.data || {};
        setError(data.message || '');
        setFieldErrors(data.errors || {});
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const onSubmit = (event) => {
    event.preventDefault();
    setError('');
    setFieldErrors({});
    window.dispatchEvent(new Event('processing'));
    mutate();
  };

  const fields = [
    { name: 'code', label: 'Recovery code', type: 'text', placeholder: '6-digit code', autoComplete: 'one-time-code' },
    { name: 'email', label: 'Email', type: 'email', placeholder: 'you@example.com', autoComplete: 'username' },
    { name: 'password', label: 'New password', type: 'password', placeholder: 'At least 8 characters', autoComplete: 'new-password' },
    { name: 'password_confirmation', label: 'Confirm password', type: 'password', placeholder: 'Repeat your new password', autoComplete: 'new-password' },
  ];

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-deepspace flex items-start sm:items-center justify-center px-3 sm:px-4 py-6 sm:py-8">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200" />
        <div className="hidden sm:block absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="hidden sm:block absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-purple/10 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      <div className="relative max-w-2xl w-full z-20">
        <div className="relative bg-dark-card/80 backdrop-blur-xl border border-dark-border/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-12 shadow-[0_20px_60px_rgb(0,0,0,0.6)]">
          <button
            type="button"
            onClick={() => navigate('/forgot')}
            aria-label="Go back"
            className="absolute left-4 top-4 sm:left-6 sm:top-6 text-text-primary hover:text-cyber-blue transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          <div className="text-center mb-8 pt-8">
            <img
              src="/images/logos/trust-vault-logo-light.png"
              alt="Trust Vault LLC"
              className="h-12 w-auto mx-auto mb-5 object-contain"
            />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-3">
              Choose a new
              <span className="block bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
                password
              </span>
            </h1>
            <p className="text-text-secondary">
              Enter the code from your email, then set a new password.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-cyber-red/10 border border-cyber-red/30 rounded-xl text-cyber-red">
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={onSubmit}>
            {fields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-semibold text-text-primary mb-2" htmlFor={field.name}>
                  {field.label}
                </label>
                <input
                  id={field.name}
                  type={field.type}
                  name={field.name}
                  value={form[field.name]}
                  onChange={onChange}
                  placeholder={field.placeholder}
                  autoComplete={field.autoComplete}
                  required
                  className={fieldClass}
                />
                {fieldErrors[field.name] && (
                  <p className="mt-1 text-sm text-cyber-red">
                    {Array.isArray(fieldErrors[field.name]) ? fieldErrors[field.name][0] : fieldErrors[field.name]}
                  </p>
                )}
              </div>
            ))}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update password'}
            </button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-cyber-blue hover:text-cyber-purple">
                Back to sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
